import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { JiraAppSelectorIconWithGithub } from '@atlassian/search-common/jira-app-selector-with-github';

import {
	GITHUB_ICON_EXPERIMENT_DEFAULT,
	GITHUB_ICON_EXPERIMENT_NAME,
	GITHUB_ICON_EXPERIMENT_PARAM,
} from '../../../../common/constants/experiments';
import { CommandIcon } from '../../../../common/constants/icons/command';
import { ReturnIcon } from '../../../../common/constants/icons/return';
import {
	type PrimaryProductKey,
	PrimaryProductKeys,
	useProductConfigs,
} from '../../../../common/constants/products';
import { useAppContext } from '../../../../common/ui/app-context';
import { SearchFooter } from '../../../../common/ui/quick-find/search-dialog-footer';
import { isOnJiraFullPageSearch } from '../../../../common/utils/is-on-jira-full-page-search';
import { getRemainingAppCount } from '../../../../common/utils/quick-find/get-remaining-app-count';
import { type State } from '../../../../common/utils/quick-find/state';
import { AllAppsIcon } from '../../../../controllers/all-apps-icon';
import { isUserOAuthed } from '../../../../controllers/oauth';
import { type QuickFindFooterEvent } from '../../../../controllers/quick-find/utils';
import {
	useAvailableProducts,
	usePrimaryProduct,
	useThirdPartyConfigs,
} from '../../../../controllers/store/bootstrap';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';
import { useSearchActions } from '../../../../controllers/store/search';

import { messages } from './messages';
import { useJiraAllAppsIcon } from './use-jira-all-apps-icon';

type Props = {
	state: State;
	onClick: (props: QuickFindFooterEvent) => void;
};

const moreButtonStyles = xcss({
	alignItems: 'center',
	backgroundColor: 'color.background.neutral',
	borderRadius: token('radius.small'),
	display: 'flex',
	paddingLeft: 'space.100',
	paddingRight: 'space.075',
});

const allAppsIconsStyles = xcss({
	display: 'flex',
	translate: token('space.050'),
});

const styles = cssMap({
	moreButtonStyles: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		display: 'flex',
		marginLeft: token('space.050'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
});

export const AllAppsIconComponent: ({
	product,
}: {
	product?: PrimaryProductKey;
}) => React.JSX.Element = ({ product }: { product?: PrimaryProductKey }) => {
	const { formatMessage } = useIntl();
	const allAppsIcon = useJiraAllAppsIcon(product);
	const availableProducts = useAvailableProducts();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const productConfigs = useProductConfigs();

	const showGitHubIcon = useMemo(
		() =>
			// Need to precondition on FG to not over expose the experiment
			// eslint-disable-next-line @atlaskit/platform/no-preconditioning
			fg('search_all_apps_icon_app_count') &&
			product === PrimaryProductKeys.Jira &&
			FeatureGates.getExperimentValue<string>(
				GITHUB_ICON_EXPERIMENT_NAME,
				GITHUB_ICON_EXPERIMENT_PARAM,
				GITHUB_ICON_EXPERIMENT_DEFAULT,
			) === 'true',
		[product],
	);

	if (fg('search_all_apps_icon_app_count')) {
		const remainingAppCount = getRemainingAppCount(
			availableProducts,
			thirdPartyConfigs,
			productConfigs,
			isUserOAuthed,
		);
		const countLabel =
			remainingAppCount > 0
				? formatMessage(messages.moreAppsCount, { count: remainingAppCount })
				: formatMessage(messages.moreApps, { more: formatMessage(messages.more) });

		return (
			<Flex role="presentation">
				{showGitHubIcon ? <JiraAppSelectorIconWithGithub /> : <AllAppsIcon />}

				<Box xcss={styles.moreButtonStyles}>
					<Text size="small" align="center" color="color.text.subtle">
						{countLabel}
					</Text>
				</Box>
			</Flex>
		);
	}

	return (
		<Flex role="presentation">
			<Box xcss={allAppsIconsStyles}>{allAppsIcon}</Box>

			<Box xcss={moreButtonStyles}>
				<Text size="small" align="center" color="color.text.subtle">
					{formatMessage(messages.moreApps, { more: formatMessage(messages.more) })}
				</Text>
			</Box>
		</Flex>
	);
};

export const SearchPageLink: ({ state, onClick }: Props) => React.JSX.Element = ({
	state,
	onClick,
}: Props): React.JSX.Element => {
	const intl = useIntl();

	const { generateSearchUrl, queryParams } = useAppContext();

	const [primaryProduct] = usePrimaryProduct();
	const quickFindQuery = useQuickFindQuery();
	const { clearAllFilters } = useSearchActions();
	const onResultClicked = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) => {
			onClick({
				state,
				event,
				actionSubjectId: `${primaryProduct?.toLowerCase() || ''}AdvancedSearchLink`,
			});

			// Executing a search query on FPS search will always clear the filters in QF as FPS filters
			// is the source of truth for filter state.
			clearAllFilters();
		},
		[onClick, state, primaryProduct, clearAllFilters],
	);

	const swapQuickFindCTAs =
		isOnJiraFullPageSearch() &&
		primaryProduct &&
		primaryProduct === PrimaryProductKeys.Jira &&
		fg('jira_search_fps_cta_swap');

	const icon = (
		<SearchIcon
			label={fg('ai-a11y-111-19638-35211') ? '' : intl.formatMessage(messages.searchAcrossProducts)}
			color="currentColor"
		/>
	);

	const title = intl.formatMessage(messages.searchAcrossProducts);

	const rightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<AllAppsIconComponent product={primaryProduct} />
			{primaryProduct === PrimaryProductKeys.Jira && !swapQuickFindCTAs && <CommandIcon />}
			<ReturnIcon />
		</Inline>
	);

	const url = generateSearchUrl({
		...queryParams,
		text: quickFindQuery,
	});

	return (
		<SearchFooter
			icon={icon}
			title={title}
			rightMetadata={rightMetadata}
			url={url}
			onResultClicked={onResultClicked}
		/>
	);
};
